import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";

import {
  addDays,
  addYears,
  differenceInDays,
  differenceInMonths,
  getOverlappingDaysInIntervals,
  isAfter,
  isFirstDayOfMonth,
  isLastDayOfMonth,
} from "date-fns";
import { kebabCase } from "lodash";
import { toast } from "react-hot-toast";
import { Button } from "@hydra/atom/components";
import { useNavigate } from "react-router-dom";
import { selectActiveCompany } from "@/store/appSlice";
import { TableWithCheckbox } from "@/components/finance/account-receivables";
import { AlertModal } from "@/components/modals";
import { BoxedContent, FormLeftHeader } from "@/components/common";
import { useModal } from "@/hooks";
import { getAssetDetailTable } from "@/components/finance/account-receivables/tableWithCheckboxData";
import { DynamicFormContainer } from "@/components/dynamic";
import dynamicObjectMap from "@/utils/maps/dynamicObjectMap";
import { getDynamicObjectRecords } from "@/api/dynamic/dynamicObjectNameApi";
import showToast from "@/utils/toast/helpers";
import {
  formatDecimalValues,
  getTestId,
} from "@/utils/helpers";
import { sectionTypeIcons } from "@/utils/dynamic/constants.js";
import useTaskRedirect from "@/hooks/useTaskRedirect";

function calculateDepreciationBasedOnDuration(item, date) {
  const {
    usefulLife,
    serviceStartDate,
    purchasePrice,
    salvageValue,
    depreciationMethod,
    depreciationDate,
  } = item;

  if (depreciationMethod !== "StraightLine") {
    return {
      depreciationForPeriod: 0,
      depreciationRate: 0,
      overlappingDays: 0
    };
  }
  let depreciationFromDate = new Date(serviceStartDate);

  if (depreciationDate) {
    depreciationFromDate = addDays(new Date(depreciationDate), 1);
  }

  const isFromDateFirstDayOfMonth = isFirstDayOfMonth(depreciationFromDate);
  const isToDateLastDayOfMonth = isLastDayOfMonth(date);
  const isDepreciationFromDateFirstDayOfMonth = isFirstDayOfMonth(new Date(depreciationFromDate));

  const annualDepreciation = (purchasePrice - salvageValue) / usefulLife;
  const depreciationRate = formatDecimalValues(Number((annualDepreciation / purchasePrice) * 100));
  const retirementDate = addYears(new Date(serviceStartDate), usefulLife);
  const numberOfDays = differenceInDays(new Date(date), new Date(depreciationFromDate));
  const overlappingDays = getOverlappingDaysInIntervals(
    {
      start: new Date(depreciationFromDate),
      end: new Date(retirementDate),
    },
    {
      start: depreciationFromDate,
      end: date,
    }
  );

  const areDaysEqual = overlappingDays === numberOfDays;

  if (areDaysEqual && overlappingDays === 365) {
    return {
      depreciationForPeriod: annualDepreciation,
      depreciationRate,
      overlappingDays
    };
  }

  if (areDaysEqual && isFromDateFirstDayOfMonth && isToDateLastDayOfMonth) {
    const diffInMonths = differenceInMonths(date, depreciationFromDate) + 1;

    const depreciationForPeriod = formatDecimalValues(
      Number((diffInMonths / 12) * annualDepreciation));

    return {
      depreciationForPeriod,
      depreciationRate,
      overlappingDays
    };
  }

  if (!areDaysEqual && isDepreciationFromDateFirstDayOfMonth && isToDateLastDayOfMonth) {
    const diffInMonths = differenceInMonths(date, new Date(depreciationFromDate)) + 1;

    const depreciationForPeriod = formatDecimalValues(
      Number((diffInMonths / 12) * annualDepreciation));

    return {
      depreciationForPeriod,
      depreciationRate,
      overlappingDays
    };
  }

  const depreciationForPeriod = formatDecimalValues(
    Number((overlappingDays / 365) * annualDepreciation));

  return {
    depreciationForPeriod,
    depreciationRate,
    overlappingDays
  };
}

const filterAsset = (asset, date) => {
  const {
    serviceStartDate, depreciationDate, purchasePrice, usefulLife
  } = asset;

  if (!purchasePrice || !usefulLife) {
    return false;
  }

  if (depreciationDate && isAfter(new Date(date), new Date(depreciationDate))) {
    return true;
  }

  if (serviceStartDate && isAfter(new Date(date), new Date(serviceStartDate))) {
    return true;
  }

  return false;
};

const formatAssets = (data, date, transactionDate, company) => {
  const items = data
    ?.filter((i) => filterAsset(i, date))
    ?.map((item) => {

      const { depreciationForPeriod, depreciationRate, overlappingDays } =
      calculateDepreciationBasedOnDuration(
        item,
        date
      );
      const numberOfDays = overlappingDays;
      return {
        company: {
          label: company.name,
          value: company.id
        },
        skip: false,
        asset: {
          label: item.itemName,
          value: item.id,
        },
        key: item.id,
        isSelected: true,
        category: item.category,
        purchaseDate: new Date(item.purchaseDate),
        rate: depreciationRate,
        cost: item.purchasePrice,
        method: item.depreciationMethod,
        totalDepreciated: item.accumulatedDepreciation || 0,
        bookValue: item.bookValue,
        depreciationForPeriod,
        description: `Depreciation for ${item.number}`,
        numberOfDays,
        depreciatedTill: new Date(date),
        transactionDate: new Date(transactionDate),
      };
    });

  const totalOpenAssetsAmount = items
    ?.filter((i) => !i.skip)
    ?.reduce((prevValue, { bookValue }) => prevValue + bookValue, 0);

  return {
    assetDetail: items,
    totalOpenAssetsAmount,
  };
};

function DepreciationForm() {
  const activeCompany = useSelector(selectActiveCompany);
  const ref = useRef(null);
  const [state, setState] = useState({
    isLoading: false,
  });
  const [assetDetailData, setAssetDetailData] = useState({});
  const { isOpen, closeModal, openModal } = useModal(false);
  const [toastId, setToastId] = useState("");
  const [selectedData, setSelectedData] = useState([]);
  const navigate = useNavigate();
  const { redirect } = useTaskRedirect();

  const { data: assetData, isLoading } = useQuery(
    [kebabCase(dynamicObjectMap.get("ItemObjectName")), "Active"],
    () =>
      getDynamicObjectRecords(dynamicObjectMap.get("ItemObjectName"), {
        sortBy: "CreatedAt",
        sortType: "DESC",
        status: "Active",
        itemType: "Asset",
        isDepreciated: true,
      })
  );

  const setAssetDetail = () => {
    const formState = ref.current.getState();
    const {
      fromDate, toDate, transactionDate, date
    } = formState;
    const numberOfDays = differenceInDays(new Date(toDate), new Date(fromDate));
    const { assetDetail } = formatAssets(
      assetData?.data, date, transactionDate, activeCompany);

    toast.dismiss(toastId);

    if (!assetDetail?.length) {
      showToast("No assets found for the given date range!", "info");
    } else {
      showToast("Assets loaded!", "success");
    }
    ref.current.setFormState({
      numberOfDays,
    });
    setAssetDetailData(assetDetail);
    openModal();
  };

  const setTotalOpenAssetsAmount = (key, value) => {
    const formState = ref.current.getState();
    formState[key] = value;
    const { assetDetail } = formState;

    const totalOpenAssetsAmount = assetDetail
      .filter((i) => !i.skip)
      .reduce((prevValue, { bookValue }) => prevValue + bookValue, 0);

    ref.current.setFormState({
      totalOpenAssetsAmount,
    });
  };

  const onStateChange = (key, value) => {
    switch (key) {
      case "date":
      case "fromDate":
      case "toDate":
      case "transactionDate": {
        setState((prevState) => ({
          ...prevState,
          [key]: value,
        }));
        break;
      }

      case "assetDetail":
        setTotalOpenAssetsAmount(key, value);
        break;

      default:
        break;
    }
  };

  useEffect(() => {
    if (!state?.date) return;
    if (isLoading) {
      const tId = toast.loading("Loading assets...");
      setToastId(tId);
    }
    if (assetData && assetData.data) {
      setAssetDetail();
    }
  }, [state?.date, assetData?.data]);

  const handleConfirm = () => {
    if (assetDetailData?.length) {
      const selectedAssets = assetDetailData.filter((q) => q.isSelected);

      if (selectedAssets) {
        const totalOpenAssetsAmount = selectedAssets
          .filter((i) => !i.skip)
          .reduce((prevValue, { bookValue }) => prevValue + bookValue, 0);

        ref.current.setFormState({
          totalOpenAssetsAmount,
          assetDetail: selectedAssets,
        });

        setSelectedData(selectedAssets);
      }
    }

    closeModal();
  };

  const handleSubmit = () => {
    const submitButton = document.getElementById(
      `dynamic-form-submit-button-${kebabCase(dynamicObjectMap.get("DepreciationObjectName"))}`
    );
    submitButton.click();
  };

  const onSuccess = (recordId) => {
    setState((prevState) => ({
      ...prevState,
      isLoading: false,
    }));

    redirect(-1, {
      recordId,
      success: true,
    });
  };

  const onError = () => {
    setState((prevState) => ({
      ...prevState,
      isLoading: false,
    }));
  };

  return (
    <BoxedContent>
      <AlertModal
        icon="file-check-stroke-icon"
        iconClass="success"
        title="Select Assets"
        subtitle="Selected date range has following active assets"
        onClose={closeModal}
        isOpen={isOpen}
        onConfirm={handleConfirm}
        size="large"
      >
        {assetDetailData?.length ? (
          <TableWithCheckbox
            data={assetDetailData}
            searchKey={["asset.label"]}
            columns={getAssetDetailTable()}
            setData={setAssetDetailData}
            selectAll
            allowMultiple
            virtualization
          />
        ) : null}
      </AlertModal>
      <DynamicFormContainer
        ref={ref}
        objectName={dynamicObjectMap.get("DepreciationObjectName")}
        showHeader
        onStateChange={onStateChange}
        hiddenFields={["assetDetail"]}
        showButtons={false}
        onSuccess={onSuccess}
        onError={onError}
        navigate={false}
        setIsLoading={(isLoadingState) =>
          setState((prevState) => ({
            ...prevState,
            isLoading: isLoadingState,
          }))}
        bulkApi="bulk-insert"
      />
      <div className="row">
        <div className="col-md-3">
          <FormLeftHeader
            title="Asset Detail"
            subtitle="Provide asset info"
            icon={sectionTypeIcons(2)}
          />
        </div>
        <div className="col-md-9">
          <TableWithCheckbox
            data={selectedData}
            searchKey={["asset.label"]}
            columns={getAssetDetailTable()}
            selectAll
            allowMultiple
            virtualization
            type="without-checkbox"
          />
        </div>
      </div>
      <div className="buttons-container buttons-at-end">
        <Button
          small
          bordered
          onClick={() => navigate(-1)}
          testId={getTestId(`${dynamicObjectMap.get("DepreciationObjectName")}-Submit-Button`)}
        >
          Cancel
        </Button>
        <Button
          small
          onClick={handleSubmit}
          loading={state.isLoading}
          testId={getTestId(`${dynamicObjectMap.get("DepreciationObjectName")}-Submit-Button`)}
        >
          Save
        </Button>
      </div>
    </BoxedContent>
  );
}

export default DepreciationForm;
